import type { ReactNode } from 'react';
import { type FieldValues, useController } from 'react-hook-form';

import { FormField, type FormFieldProps } from '~/chords/FormField';
import {
  type DropdownSingleSelectAsyncProps,
  Select,
  type SortableMultiSelectSecondaryAction,
} from '~/chords/Select';

type FormFieldDropdownSortableMultiSelectAsyncProps<
  T,
  K,
  TFormFields extends FieldValues,
> = Omit<
  FormFieldProps<TFormFields>,
  | 'children'
  | 'dangerouslyOverrideClassName'
  | 'triggerClassName'
  | 'fieldMessageClassName'
  | 'labelAsSpan'
> &
  Omit<
    DropdownSingleSelectAsyncProps<T, K>,
    'onChange' | 'defaultValue' | 'resolveFn' | 'defaultValueErrorMessage'
  > & {
    onValueChange?: (items: T[]) => void;
    getItemImage?: (item: T) => string | undefined;
    renderHeader?: (item: T) => ReactNode;
    /** @deprecated Use renderHeader instead */
    renderContent?: (item: T) => ReactNode;
    renderBody?: (item: T) => ReactNode;
    secondaryActions?: SortableMultiSelectSecondaryAction<T>[];
    removeLabel?: string;
  };

export function FormFieldDropdownSortableMultiSelectAsync<
  T,
  K,
  TFormFields extends FieldValues = FieldValues,
>(props: FormFieldDropdownSortableMultiSelectAsyncProps<T, K, TFormFields>) {
  const {
    name,
    id,
    label,
    disabled,
    readOnly,
    labelAddon,
    showErrorMessage,
    onValueChange,
    getOptionValue,
    getOptionLabel,
    onOpenChange,
    queryFn,
    getItemImage,
    renderHeader,
    // eslint-disable-next-line @typescript-eslint/no-deprecated
    renderContent,
    renderBody,
    secondaryActions,
    removeLabel,
    ...dropdownProps
  } = props;

  const {
    field: { ref: _ref, value, onChange, onBlur },
    fieldState: { invalid },
  } = useController<TFormFields>({ name, disabled });

  const selectedItems = (value ?? []) as T[];

  return (
    <FormField
      name={name}
      id={id}
      label={label}
      disabled={disabled}
      readOnly={readOnly}
      labelAddon={labelAddon}
      showErrorMessage={showErrorMessage}
    >
      <Select.MultiSortable<T>
        value={selectedItems}
        onValueChange={(items) => {
          onChange(items);
          onBlur();
          onValueChange?.(items);
        }}
        getItemLabel={getOptionLabel}
        getItemValue={(item) => String(getOptionValue(item))}
        getItemImage={getItemImage}
        renderHeader={renderHeader}
        // eslint-disable-next-line @typescript-eslint/no-deprecated
        renderContent={renderContent}
        renderBody={renderBody}
        secondaryActions={secondaryActions}
        removeLabel={removeLabel}
        disabled={disabled || readOnly}
        renderDropdown={({
          onSelect,
          selectedValues,
          resetKey,
          disabled: isDisabled,
        }) => (
          <Select.SingleAsync<T, K>
            key={resetKey}
            id={id || name}
            {...dropdownProps}
            getOptionValue={getOptionValue}
            getOptionLabel={getOptionLabel}
            queryFn={async (search) => {
              const results = await queryFn(search);

              return results.filter(
                (item) => !selectedValues.has(String(getOptionValue(item))),
              );
            }}
            onChange={onSelect}
            onOpenChange={(isOpen) => {
              if (!isOpen) {
                onBlur();
              }

              onOpenChange?.(isOpen);
            }}
            state={invalid ? 'error' : 'default'}
            disabled={isDisabled}
          />
        )}
      />
    </FormField>
  );
}

FormFieldDropdownSortableMultiSelectAsync.displayName =
  'FormFieldDropdownSortableMultiSelectAsync';
